import { observable, action, makeObservable } from 'mobx';
import moment from 'moment/min/moment-with-locales';
import queryString from 'query-string';
import _ from 'lodash';
import agent from '../agent';
import { calculateBusinessDays, calculateHoursFromTimes } from '../../utils/commonMethods';
import { FULL_DAY_ONLY_ABSENCE_TYPES } from '../../utils/constants';
import commonStore from './commonStore';

let absenceAddressThrottleTimer = null;
class AbsenceStore {
    constructor() {
        makeObservable(this, {
            currentList: observable,
            currentEntity: observable,
            absenceStatusCounts: observable,
            updatingErrors: observable,
            updating: observable,
            loading: observable,
            requestParams: observable,
            deleteSuccess: observable,
            lastListLoadTime: observable,
            standardTimeBasedOnUserProfile: observable,
            defaultCompanyStartTime: observable,
            defaultCompanyEndTime: observable,
            userBreakDetails: observable,
            filters: observable,
            appliedFilters: observable,
            standardCompanyTimeInMins: observable,
            currentUserExtraPayments: observable,
            currentUserKids: observable,
            startTime: observable,
            endTime: observable,
            isFromSchedule: observable,
            editAbsenceUserId: observable,
            flag: observable,
            resetLastListLoadTime: action,
            setFilter: action,
            onFilter: action,
            loadList: action,
            recalc: action,
            returnDefaultNew: action,
            load: action,
            save: action,
            remove: action,
            resetSelectedChild: action,
            getAbsenceTotals: action,
            cancelAbsence: action,
            saveCompOff: action,
            updateAbsenceTime: action,
            lastInteractedId: observable,
            setLastInteractedId: action,
            clearLastInteractedId: action,
        });
    }

    currentList = [];
    currentEntity = {};
    absenceStatusCounts = {
        all: 0,
        approved: 0,
        active: 0,
    };
    updatingErrors = null;
    updating = false;
    loading = false;
    requestParams = null;
    deleteSuccess = false;
    lastListLoadTime = null;
    lastInteractedId = null;
    standardCompanyTimeInMins = 450;
    currentUserExtraPayments = "";
    standardTimeBasedOnUserProfile = 450;
    defaultCompanyStartTime = 480;
    defaultCompanyEndTime = 960;
    userBreakDetails = null;
    currentUserKids = null;
    currentUserShiftDetails = null;
    startTime = null;
    endTime = null;
    editAbsenceUserId = null;
    isFromSchedule = false;
    flag = false;
    filters = {
        status: '',
        name: '',
        absence_type: '',
    };
    appliedFilters = {
        status: '',
        name: '',
        absence_type: '',
    };

    resetLastListLoadTime(value) {
        this.lastListLoadTime = value;
    }

    setLastInteractedId(id) {
        this.lastInteractedId = id ?? null;
    }

    clearLastInteractedId() {
        this.lastInteractedId = null;
    }

    setFilter(name, value) {
        const filters = Object.assign({}, this.filters);
        filters[name] = value;
        this.filters = filters;
    }

    onFilter() {
        this.appliedFilters = Object.assign({}, this.filters);
    }

    loadList(params) {
        return agent.Absences.list(params)
            .then(
                action(list => {
                    this.requestParams = params;
                    list.time = new Date();
                    this.lastListLoadTime = list.time;
                    this.currentList = list;
                    return list;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    async recalcGpsForAbsence(changes) {
        const entityToAddressString = e => {
            return `${e.address}, ${e.post_place}, ${e.post_number}`;
        };

        if (!changes.address || !changes.post_place) {
            return changes;
        }

        const newAddress = entityToAddressString(changes);

        if (newAddress === this.currentEntity.absence.data.addressCache) return changes;

        absenceAddressThrottleTimer = new Date().getTime();
        setTimeout(() => {
            // throttle
            if (new Date().getTime() - absenceAddressThrottleTimer < 1000) {
                return;
            }
            if (this.currentEntity.absence.data.addressCache === newAddress) {
                return;
            }
            this.performRecalcGpsForAbsence(newAddress);
        }, 1000);
        return changes;
    }

    async performRecalcGpsForAbsence(newAddress) {
        this.gpsNowLoading = true;
        this.currentEntity.absence.data.addressCache = newAddress;
        const tsk = _.cloneDeep(this.currentEntity.absence);
        try {
            const data = await agent.Tasks.gpsAddress(newAddress, null, false);
            tsk.gps_data = data.result;
            tsk.data.addressCache = newAddress;
            this.currentEntity = { absence: tsk };
            this.gpsNowLoading = false;
        } catch (e) {
            this.gpsNowLoading = false;
            throw e;
        }
    }



    async recalc(data, defaultHolidaysForVacation, enableDefaultHolidaysForAbsence, getId) {
        let isEmpPercentChanged = false;
        if (!data || !this.currentEntity.absence) return;
        if (data && data.user_id) {
            this.currentEntity.absence.user_id = data.user_id;
            this.currentEntity.absence.userBreakDetails = this.userBreakDetails;
        }

        if (data && data.project_id) {
            this.currentEntity.absence.project_id = data.project_id;
        }

        if (data?.employement_grade !== undefined && this.standardCompanyTimeInMins &&
            data.employement_grade !== this.currentEntity.absence.employement_grade) {
            const newMinutes = Math.round(this.standardCompanyTimeInMins * (data.employement_grade / 100));
            data.end_time = data.start_time + newMinutes;
            isEmpPercentChanged = true;
        } else if (data?.employement_grade === undefined) {
            isEmpPercentChanged = true;
            const newMinutes = Math.round(this.standardCompanyTimeInMins * (1 / 100));
            data.end_time = data.start_time + newMinutes;
        }

        if (data && (this.currentEntity.absence.from !== data.from || this.currentEntity.absence.to !== data.to)) {
            const params = {
                user_id: data.user_id,
                from: moment(data.from).format('YYYY-MM-DD'),
                to: moment(data.to).format('YYYY-MM-DD'),
                editAbsenceId: getId ? getId : '',
            }
            await this.getAbsenceTotals(params);
        }
        if (data && data.user_id && this.standardCompanyTimeInMins &&
            this.startTime !== undefined && this.endTime !== undefined
            && (this.currentEntity.absence.start_time !== this.startTime || this.currentEntity.absence.end_time !== this.endTime)
            && (!getId || getId && (this.editAbsenceUserId !== data.user_id || !this.flag))) {
            this.flag = false;
            this.currentEntity.absence.start_time = this.startTime;
            this.currentEntity.absence.end_time = this.endTime;
            data.start_time = this.startTime;
            data.end_time = this.endTime;
        }

        if (data && data.absence_type && this.standardCompanyTimeInMins &&
            this.startTime !== undefined && this.endTime !== undefined
            && data.absence_type !== this.currentEntity.absence.absence_type
            && FULL_DAY_ONLY_ABSENCE_TYPES.includes(data.absence_type)) {
            this.currentEntity.absence.start_time = this.startTime;
            this.currentEntity.absence.end_time = this.endTime;
            data.start_time = this.startTime;
            data.end_time = this.endTime;
        }

        let pay = 0;
        if (data && data.absence_payment_per_day && data.absence_payment_per_day > 0) {
            pay = data.absence_payment_per_day;
        }

        if (data && data.from) {
            const from = new Date(data.from);
            const to = new Date(data.to);
            if (from > to) {
                data.to = data.from;
                this.currentEntity.absence.to = data.to;
            }
        }

        const from = data ? data.from : this.currentEntity.absence.from;
        const to = data ? data.to : this.currentEntity.absence.to;
        let days = calculateBusinessDays(from, to, defaultHolidaysForVacation, enableDefaultHolidaysForAbsence, commonStore, 'absence');
        this.currentEntity.absence.from = from;
        this.currentEntity.absence.to = to;
        this.currentEntity.absence.total_days = days;
        this.currentEntity.absence.absence_type = data && data.absence_type ? data.absence_type : this.currentEntity.absence.absence_type;
        data.total_days = days;
        const workRelatedAbsenceTypes = commonStore.config.client?.data?.basicRules?.workRelatedAbsenceTypes || [];
        data.is_work_related_leave = data && workRelatedAbsenceTypes.includes(data.absence_type) ? data.is_work_related_leave : false;

        if (data && data.address && data.post_number) {
            this.currentEntity.absence.address = data.address;
            this.currentEntity.absence.post_number = data.post_number;
            await this.recalcGpsForAbsence(data);
        }

        if (data && data.post_place) this.currentEntity.absence.post_place = data.post_place;
        if (data && data.start_time !== undefined && data.end_time !== undefined
            && data.leave_type && (data.start_time !== 0 || data.end_time !== 0)
        ) {
            const calculatedHours = calculateHoursFromTimes(data.start_time, data.end_time) || 0;
            this.currentEntity.absence.hours = Number(calculatedHours) || 0;
            data.hours = calculatedHours;
            data.totalHours = calculatedHours; //added for validation in form
            if (this.standardCompanyTimeInMins) {
                const actualWorkingMinutes = calculatedHours * 60;
                const percent = Number(((actualWorkingMinutes * 100) / this.standardCompanyTimeInMins).toFixed(2));
                this.currentEntity.absence.employement_grade = (percent >= 99 && percent <= 101) ? 100 : percent;
            } else {
                this.currentEntity.absence.employement_grade = 100;
                this.currentEntity.absence.start_time = 0;
                this.currentEntity.absence.end_time = 0;
                data.start_time = 0;
                data.end_time = 0;
            }
            if (!isEmpPercentChanged) {
                data.employement_grade = this.currentEntity.absence.employement_grade;
            }
        }

        pay = Number((pay * data.hours).toFixed(2));

        data.total_hours = Number(Number((days) * data.hours).toFixed(2));
        this.currentEntity.absence.total_hours = data.total_hours;
        this.currentEntity.absence.absence_total_payment = pay * (days);
        data && (data.absence_total_payment = this.currentEntity.absence.absence_total_payment);
    }

    returnDefaultNew(params) {
        this.currentEntity = {
            absence: {
                user_id: params.user_id,
                from: params.currentDate,
                to: params.currentDate,
                total_days: 1,
                absence_type: params.absence_type ? params.absence_type : 'self',
                absence_payment_per_day: 0,
                status: 'active',
                data: {},
            },
        };
        if (params && params.location) {
            const search = queryString.parse(params.location.search);
            if (search.from) {
                this.currentEntity.absence.from = moment(new Date(search.from)).format('YYYY-MM-DD');
            }
            if (search.to) {
                this.currentEntity.absence.to = moment(new Date(search.to)).format('YYYY-MM-DD');
            }
        }
        this.recalc();
        this.loading = false;
    }

    load(id) {
        this.loading = true;
        return agent.Absences.load(id)
            .then(
                action(response => {
                    if (!response.absence.data) response.absence.data = {};
                    if (response.absence.data.child_index) {
                        response.absence.child = response.absence.data.child_index;
                    }
                    if (response.absence.data.is_work_related_leave) {
                        response.absence.is_work_related_leave = response.absence.data.is_work_related_leave;
                    }
                    if (response.absence.data.social_welfare_leave_type) {
                        response.absence.social_welfare_leave_type = response.absence.data.social_welfare_leave_type;
                    }
                    if (!response.absence.data.doctor_certification_date) {
                        delete response.absence.data.doctor_certification_date;
                    }
                    this.currentEntity = response;
                    this.currentEntity.absence.from = moment(this.currentEntity.absence.from).format('YYYY-MM-DD');
                    this.currentEntity.absence.to = moment(this.currentEntity.absence.to).format('YYYY-MM-DD');
                    this.editAbsenceUserId = response.absence.user_id;
                    this.flag = true;
                    this.loading = false;
                    return response;
                })
            )
            .catch(
                action(err => {
                    this.loading = false;
                    throw err;
                })
            );
    }

    resetSelectedChild() {
        this.currentEntity.absence.child = null;
    }

    save(values, isAdd) {
        this.updating = true;
        if (!values.data) values.data = {};
        if (values.child) {
            if (this.currentUserKids && this.currentUserKids[parseInt(values.child, 10)]) {
                values.data.child = this.currentUserKids[parseInt(values.child, 10)];
            } else {
                values.data.child = null;
            }
            values.data.child_index = values.child;
        }
        if (values.is_work_related_leave) {
            values.data.is_work_related_leave = true;
        } else {
            values.data.is_work_related_leave = false;
        }
        if (values.social_welfare_leave_type) {
            values.data.social_welfare_leave_type = values.social_welfare_leave_type;
        } else {
            values.data.social_welfare_leave_type = null;
        }
        values.is_web = true;
        return agent.Absences.save(values, isAdd)
            .then(
                action(user => {
                    this.currentEntity = user;
                    this.updating = false;
                    return user;
                })
            )
            .catch(
                action(err => {
                    this.updating = false;
                    this.updatingErrors = err.response && err.response.body && err.response.body.errors;
                    if (err.response?.body)
                        return err.response.body
                    throw err;
                })
            );
    }

    async remove(id, data) {
        return agent.Absences.remove(id, data).then(res => {
            return res;
        }).catch(err => {
            throw err;
        });
    }

    getAbsenceTotals(params) {
        return agent.Absences.getAbsenceTotals(params)
            .then(
                action(data => {
                    if (this.currentEntity?.absence) {
                        this.standardCompanyTimeInMins = data.employeeDailyContractedTimeInMins;
                        this.currentUserExtraPayments = data.employeeExtraPayments;
                        this.startTime = data.startTime;
                        this.endTime = data.endTime > 1440 ? data.endTime - 1440 : data.endTime;
                        this.isFromSchedule = data.isTimeFromSchedule;
                        this.standardTimeBasedOnUserProfile = data.standardTimeBasedOnUserProfile;
                        this.defaultCompanyStartTime = data.defaultCompanyStartTime;
                        this.defaultCompanyEndTime = data.defaultCompanyEndTime > 1440 ? data.defaultCompanyEndTime - 1440 : data.defaultCompanyEndTime;
                        this.userBreakDetails = data.userBreakDetails;
                    }
                    return data;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    getChildrenDetails(id) {
        return agent.Absences.getChildrenDetails(id)
            .then(
                action(data => {
                    this.currentUserKids = data.kids ? data.kids : []
                    return data.kids ? data.kids : [];
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    cancelAbsence(id, data) {
        return agent.Absences.cancelAbsence(id, data)
            .then(
                action(res => {
                    return res;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    saveCompOff(values) {
        this.loading = true;
        return agent.Absences.saveCompOff(values)
            .then(
                action(user => {
                    this.loading = false;
                    return user;
                })
            )
            .catch(
                action(err => {
                    this.loading = false;
                    throw err;
                })
            );
    }

    getAbsenceTotalsCounts() {
        return agent.Absences.getAbsenceStatusCounts()
            .then(
                action(response => {
                    const { all, active, approved } = response;
                    this.absenceStatusCounts.all = all;
                    this.absenceStatusCounts.active = active;
                    this.absenceStatusCounts.approved = approved;
                    return response;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    updateAbsenceTime(time) {
        this.startTime = time.start_time;
        this.endTime = time.end_time;
    }
}

const _AbsenceStore = new AbsenceStore();
export default _AbsenceStore;